// ---------------------------------------------------------------------------
// CSV export — shared writer
//
// The counterpart to lead-csv.ts (which PARSES imported CSV/Excel). This module
// writes CSV: the RFC-4180 field escaping, the header+rows assembly, the browser
// download, and the campaign-leads column set used by the "Export Leads" button.
//
// The escaping/assembly/download primitives were extracted from wiz-leads.ts so
// its sourcing-preview export and the campaign-leads export share ONE escaper —
// a comma or quote in a German company name must be handled the same everywhere.
// ---------------------------------------------------------------------------

/**
 * RFC-4180 field escaping: quote a field iff it contains a comma, a double
 * quote, or a newline, and double any embedded quote. Everything else passes
 * through untouched. German company names and job titles routinely carry commas
 * (e.g. "Müller, Meyer & Co. GmbH"), so this is not optional.
 */
export const csvEscape = (value: string): string => (/[",\r\n]/.test(value) ? `"${value.replace(/"/g, '""')}"` : value);

/**
 * Assemble a CSV string from a header row and data rows. CRLF line endings, for
 * Excel-on-Windows compatibility (a lone \n can collapse rows there).
 */
export function rowsToCsv(headers: string[], rows: string[][]): string {
  return [headers, ...rows].map(line => line.map(csvEscape).join(',')).join('\r\n');
}

/**
 * Trigger a browser download of a text file. Browser-only (Blob / createObjectURL
 * / anchor click), so it cannot run in a node unit test — the CSV *content* is
 * built by the pure functions above, which are what the tests cover.
 */
export function downloadTextFile(filename: string, contents: string, mimeType: string): void {
  const blob = new Blob([contents], { type: mimeType });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

/**
 * The lead fields the campaign-leads export reads directly. A structural subset
 * of campaign-detail.ts's `LeadData`, so the page can pass its `LeadData[]`
 * straight in. The three DERIVED columns — effective status, sending account,
 * sales rep — are NOT read off the lead here: they are computed by the page (the
 * same helpers the table uses) and injected via `CampaignLeadCsvContext`, so the
 * exported "Status" is guaranteed to match the on-screen chip rather than a
 * re-derivation that could drift from it.
 */
export interface CampaignLeadCsvLead {
  firstName?: string;
  lastName?: string;
  email?: string;
  jobTitle?: string;
  company?: string;
  industry?: string;
  linkedinUrl?: string;
  status?: string;
  manualStatus?: string;
  source?: string;
  connectionState?: string;
  sentAt?: string;
  openedAt?: string;
  repliedAt?: string;
  bouncedAt?: string;
  dmSentAt?: string;
  connectionSentAt?: string;
  acceptedAt?: string;
  doneAt?: string;
  completedAt?: string;
  lastContactedAt?: string;
  replyText?: string;
}

/**
 * The page-computed columns. Injecting them (rather than reading the lead) keeps
 * the CSV honest: `status` is the exact chip label the row shows, and the two
 * account/rep names are resolved through the same maps the table renders — never
 * a raw provider id.
 */
export interface CampaignLeadCsvContext<L extends CampaignLeadCsvLead> {
  /** The effective on-screen status label (the chip). '' if none. */
  statusLabel: (lead: L) => string;
  /** Human name of the LinkedIn/email account that sent this lead's outreach. '' if none. */
  sendingAccount: (lead: L) => string;
  /** Assigned Sales Rep display name. '' if none. */
  salesRep: (lead: L) => string;
}

/**
 * Build the campaign-leads CSV. Generic over the lead type so the page's richer
 * `LeadData` passes in without a cast; the columns read only the subset above.
 */
export function buildCampaignLeadsCsv<L extends CampaignLeadCsvLead>(leads: L[], ctx: CampaignLeadCsvContext<L>): string {
  const columns: { header: string; get: (l: L) => string }[] = [
    { header: 'First Name', get: l => l.firstName ?? '' },
    { header: 'Last Name', get: l => l.lastName ?? '' },
    { header: 'Email', get: l => l.email ?? '' },
    { header: 'Job Title', get: l => l.jobTitle ?? '' },
    { header: 'Company', get: l => l.company ?? '' },
    { header: 'Industry', get: l => l.industry ?? '' },
    { header: 'LinkedIn URL', get: l => l.linkedinUrl ?? '' },
    // The effective status the user sees — the chip, not the raw `status`. A
    // lead can carry status:'contacted' while the chip reads 'replied' (a reply
    // arrived) or 'opted out' (globally suppressed); the CRM must get what the
    // operator sees.
    { header: 'Status', get: l => ctx.statusLabel(l) },
    // The raw override kept alongside so a CRM can see a status was set by hand.
    { header: 'Manual Override', get: l => l.manualStatus ?? '' },
    { header: 'Sales Rep', get: l => ctx.salesRep(l) },
    { header: 'Sending Account', get: l => ctx.sendingAccount(l) },
    { header: 'Connection State', get: l => l.connectionState ?? '' },
    { header: 'Sent At', get: l => l.sentAt ?? '' },
    { header: 'Opened At', get: l => l.openedAt ?? '' },
    { header: 'Replied At', get: l => l.repliedAt ?? '' },
    { header: 'Bounced At', get: l => l.bouncedAt ?? '' },
    { header: 'DM Sent At', get: l => l.dmSentAt ?? '' },
    { header: 'Invite Sent At', get: l => l.connectionSentAt ?? '' },
    { header: 'Accepted At', get: l => l.acceptedAt ?? '' },
    { header: 'Completed At', get: l => l.doneAt ?? l.completedAt ?? '' },
    { header: 'Last Contacted At', get: l => l.lastContactedAt ?? '' },
    { header: 'Source', get: l => l.source ?? '' },
    { header: 'Reply', get: l => l.replyText ?? '' },
  ];
  return rowsToCsv(
    columns.map(c => c.header),
    leads.map(lead => columns.map(c => c.get(lead))),
  );
}

/** Filename-safe slug for a campaign name, with a fallback. */
export function campaignFilenameSlug(name: string | undefined | null): string {
  const slug = (name ?? '')
    .trim()
    .replace(/[^\w-]+/g, '_')
    .replace(/^_+|_+$/g, '');
  return slug || 'campaign';
}
